/**
 * Token persistence + refresh, decoupled from React so the HTTP layer can read
 * a bearer token without going through a context. Port of
 * `app/src/auth/tokenStore.ts` for the browser.
 *
 * The one real tradeoff (web/docs/data-layer.md §Token storage): the access
 * token lives **in memory only** — it is lost on reload and re-derived from
 * the refresh token on boot. Only the refresh token (+ display metadata) is
 * persisted, under `localStorage["screenatlas.session.v1"]`. Never put the
 * access token in localStorage.
 *
 * The bearer sent on the wire is the Cognito **access token**: `/auth/refresh`
 * renews the access token but not the id token, so only the access token stays
 * valid across the long refresh window.
 */

import { emailAuth, AuthApiError } from './emailAuth';

import type { Session } from '@/auth/types';

const SESSION_KEY = 'screenatlas.session.v1';
const EXPIRY_BUFFER_MS = 60 * 1000;

/** Everything that is persisted — the session minus the access token. */
type PersistedSession = Omit<Session, 'accessToken'>;

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;
let refreshInFlight: Promise<Session | null> | null = null;

// Memory-only access token state.
let memoryAccessToken: string | null = null;
let memoryExpiresAt = 0;

export function setOnUnauthorized(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

export function loadSession(): Session | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const persisted = JSON.parse(raw) as PersistedSession;
    if (!persisted || typeof persisted !== 'object' || !persisted.provider) return null;
    return { ...persisted, accessToken: memoryAccessToken ?? '' };
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  memoryAccessToken = session.accessToken || null;
  memoryExpiresAt = session.expiresAt;
  const persisted: PersistedSession = {
    idToken: session.idToken,
    refreshToken: session.refreshToken,
    expiresAt: session.expiresAt,
    email: session.email,
    name: session.name,
    sub: session.sub,
    provider: session.provider,
  };
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(persisted));
  } catch {
    // Storage full/blocked — the in-memory token still works for this tab;
    // the session simply will not survive a reload.
  }
}

export function clearSession(): void {
  memoryAccessToken = null;
  memoryExpiresAt = 0;
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Ignore — nothing more to do.
  }
  onUnauthorized?.();
}

/**
 * Exchange the refresh token for a fresh access token via the backend.
 *   - 401 → the refresh token is dead: clear storage, fire the unauthorized
 *     handler (bounces the UI to login), return null.
 *   - any other failure (network / 5xx) → keep the stored session so a
 *     transient outage doesn't sign the user out; return it as-is.
 * Concurrent callers share one request.
 */
export function refreshSession(): Promise<Session | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const current = loadSession();
    if (!current?.refreshToken) {
      clearSession();
      return null;
    }
    try {
      const r = await emailAuth.refresh(current.refreshToken);
      const updated: Session = {
        ...current,
        accessToken: r.access_token,
        idToken: r.id_token ?? current.idToken,
        expiresAt: Date.now() + (r.expires_in ?? 3600) * 1000,
      };
      saveSession(updated);
      return updated;
    } catch (e) {
      if (e instanceof AuthApiError && e.status === 401) {
        clearSession();
        return null;
      }
      return current;
    }
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** The token to attach as `Authorization: Bearer …` (the access token). */
export async function getBearerToken(): Promise<string | null> {
  if (memoryAccessToken && memoryExpiresAt - EXPIRY_BUFFER_MS > Date.now()) {
    return memoryAccessToken;
  }
  const stored = loadSession();
  if (!stored) return null;
  if (!stored.refreshToken) {
    clearSession();
    return null;
  }
  const refreshed = await refreshSession();
  return refreshed?.accessToken || null;
}
